import cN from 'classnames';
import PropTypes from 'prop-types';
import type { ReactNode } from 'react';
import type { TabsProps } from 'react-tabs';
import { Tabs as ReactTabs, Tab, TabList, TabPanel } from 'react-tabs';
import type { EventTrackingProps } from '../../services/Tracking';
import { trackEvent } from '../../services/Tracking';
import style from './style.m.less';

const propTypes = {
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            header: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
            panel: PropTypes.element,
            disabled: PropTypes.bool,
            trackingLabel: PropTypes.string,
        })
    ),
    compact: PropTypes.bool,
    fullWidth: PropTypes.bool,
    classNames: PropTypes.shape({
        tabContainer: PropTypes.string,
        tabList: PropTypes.string,
        tab: PropTypes.string,
        panel: PropTypes.string,
        selectedPanel: PropTypes.string,
        selectedTab: PropTypes.string,
    }),
    onSelect: PropTypes.func,
    selectedIndex: PropTypes.number,
    eventTracking: PropTypes.shape({
        category: PropTypes.string.isRequired,
        action: PropTypes.string,
    }),
};

export type TabType = {
    header: ReactNode;
    panel?: ReactNode;
    disabled?: boolean;
    trackingLabel?: string;
};

type BaseProps = {
    tabs: TabType[];
    children?: ReactNode;
    classNames?: {
        tabContainer?: string;
        tabList?: string;
        tab?: string;
        panel?: string;
        selectedPanel?: string;
        selectedTab?: string;
    };
    compact?: boolean;
    fullWidth?: boolean;
    eventTracking?: EventTrackingProps;
};

type PropsUncontrolled = BaseProps & {
    selectedIndex?: never;
    onSelect?: never;
};

type PropsControlled = BaseProps & {
    selectedIndex: number;
    onSelect: NonNullable<TabsProps['onSelect']>;
};

type Props = PropsUncontrolled | PropsControlled;

const isControlled = (props: Props): props is PropsControlled => 'selectedIndex' in props || 'onSelect' in props;

export default function Tabs(props: Props) {
    'use memo';
    const { tabs, children, classNames = {}, compact, fullWidth, eventTracking } = props;
    function getInteractivityProps(): TabsProps {
        if (isControlled(props)) {
            return {
                selectedIndex: props.selectedIndex,
                onSelect: (index: number, last: number, event: Event) => {
                    if (eventTracking) {
                        trackEvent({ ...eventTracking, label: tabs[index].trackingLabel });
                    }
                    return props.onSelect?.(index, last, event);
                },
            };
        }
        // There must not be any selectedIndex or onSelect prop to use react-tabs in uncontrolled mode.
        return {};
    }
    return (
        <ReactTabs
            {...getInteractivityProps()}
            focusTabOnClick={false}
            selectedTabClassName={cN(style.selected, classNames.selectedTab)}
            disabledTabClassName={style.disabled}
            className={cN(style.container, classNames.tabContainer, {
                [style.compact]: compact,
                [style.fullWidth]: fullWidth,
            })}
        >
            <TabList className={cN(style.tabList, classNames.tabList)}>
                {tabs.map((tab, i) => (
                    <Tab key={i} className={cN(style.tab, classNames.tab)} disabled={tab.disabled}>
                        {tab.header}
                    </Tab>
                ))}
                {children ? <div className={style.tabOptions}>{children}</div> : null}
            </TabList>
            {tabs.map((tab, i) => (
                <TabPanel
                    key={i}
                    className={cN(style.tabPanel, classNames.panel)}
                    selectedClassName={classNames.selectedPanel}
                >
                    {tab.panel}
                </TabPanel>
            ))}
        </ReactTabs>
    );
}

Tabs.propTypes = propTypes;
